import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { FormGroup, Validators } from '@angular/forms';
import {
  ControlPlateType,
  ParkingProcessInfoDTO,
  ParkingProcessLimitedZoneWithParentInfoDTO,
  ParkingProcessSubscriptionAssignmentInfoDTO,
  ParkingProcessZoneInfoDTO,
  UserInfoDTO,
} from '../../base/models/models.swagger.generated';
import { ParkingService } from './parking/parking.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, Observable, of } from 'rxjs';
import { AutomaticParkingScheduleEditFormGroup } from './parking-administration/parking-administration-edit-form/parking-administration-edit-form.type';
import { ParkingFormType, VehicleFromGroup } from './parking/parking-form-type';

export interface IPlate {
  countryCode: string;
  type: ControlPlateType;
  numberPlate: string;
}

export class ParkingHelper {

  public clientDropdownValues: {name: string; id: number}[] = [];
  public subscriptionsDropdownValues: {name: string; id: number; assignment: ParkingProcessSubscriptionAssignmentInfoDTO}[] = [];
  public zoneDropdownValues: {name: string[]; id: number}[] = [];
  public parkingSpaceNumberDefinition: string;
  public controlPlateDropdownValues: {name: string; id: number; plate: IPlate}[] = [];

  public parkingProcessZoneInfoDTO: ParkingProcessZoneInfoDTO[];
  public zonesLoaded = new Subject();
  public hasFoundZones = true;
  public hasFoundSubscriptions = true;
  public hasFoundControlPlates = true;

  private user: UserInfoDTO;

  constructor(private isAutomaticParking: boolean,
    private parkingProcessInfoService: ParkingService,
    private translate: TranslateService,
    private spinnerService: SpinnerService) {}

  // user needs to be set beforehand
  public assignFormFieldSubscriptions(parkingForm: AutomaticParkingScheduleEditFormGroup | FormGroup<ParkingFormType>, subscriptions: Subscription, user: UserInfoDTO) {
    this.user = user;
    subscriptions.add(
      parkingForm.controls.clientId.valueChanges
        .pipe(
          filter(val => !!val),
          distinctUntilChanged(),
          switchMap(clientId => this.isAutomaticParking
            ? this.parkingProcessInfoService.getAutomaticParkingScheduleZoneInfos(clientId)
            : this.parkingProcessInfoService.getParkingProcessZoneInfos(clientId)),
        )
        .subscribe(parkingProcessZoneInfoDTO => {
          this.parkingProcessZoneInfoDTO = parkingProcessZoneInfoDTO;
          this.createZoneDropdownValues(parkingProcessZoneInfoDTO);
          this.hasFoundZones = this.zoneDropdownValues?.length > 0;
          const zoneValue = this.zoneDropdownValues?.find(z => z.id === parkingForm.controls.zoneId.getRawValue());
          if (zoneValue == null) {
            if (this.zoneDropdownValues?.length === 1) {
              parkingForm.controls.zoneId.setValue(this.zoneDropdownValues[0].id, {onlySelf: true});
              parkingForm.controls.zoneId.disable();
            } else {
              parkingForm.controls.zoneId.setValue(null, {onlySelf: true});
              parkingForm.controls.zoneId.enable();
            }
          }
        })
    );

    subscriptions.add(
      parkingForm.controls.zoneId.valueChanges.pipe(distinctUntilChanged()).subscribe(zoneId => {
        this.createSubscriptionDropdownValues(zoneId);
        if (zoneId) {
          this.hasFoundSubscriptions = this.subscriptionsDropdownValues?.length > 0;
        }
        const subscription = this.subscriptionsDropdownValues?.find(s => s.id === parkingForm.controls.subscriptionAssignmentId.getRawValue());
        if (subscription == null) {
          if (this.subscriptionsDropdownValues?.length === 1) {
            parkingForm.controls.subscriptionAssignmentId.setValue(this.subscriptionsDropdownValues[0].id, {onlySelf: true});
            parkingForm.controls.subscriptionAssignmentId.disable();
          } else {
            parkingForm.controls.subscriptionAssignmentId.setValue(null, {onlySelf: true});
            parkingForm.controls.subscriptionAssignmentId.enable();
          }
        }
      })
    );

    subscriptions.add(
      parkingForm.controls.subscriptionAssignmentId.valueChanges.pipe(distinctUntilChanged()).subscribe(subscriptionId => {
        this.handleVehicleFields(this.user, subscriptionId, parkingForm.controls.zoneId.value);
        if (subscriptionId) {
          this.hasFoundControlPlates = this.controlPlateDropdownValues?.length > 0;
        }
        if (this.controlPlateDropdownValues?.length === 1) {
          parkingForm.controls.controlPlateId.setValue(this.controlPlateDropdownValues[0].id, {onlySelf: true});
          parkingForm.controls.controlPlateId.disable();
        } else {
          parkingForm.controls.controlPlateId.enable();
        }
      })
    );

    if ('newVehicle' in parkingForm.controls) {
      let vehicleFormGroup = (parkingForm as FormGroup<ParkingFormType>).controls.newVehicle as VehicleFromGroup;
      subscriptions.add(
        parkingForm.controls.controlPlateId.valueChanges.subscribe(controlPlateId => {
          if (controlPlateId === -1) {
            vehicleFormGroup.controls.type.setValidators([Validators.required]);
            vehicleFormGroup.controls.type.setValue(null);
            vehicleFormGroup.controls.countryCode.setValidators([Validators.required]);
            vehicleFormGroup.controls.countryCode.setValue(null);
            vehicleFormGroup.controls.numberPlate.setValidators([Validators.required]);
            vehicleFormGroup.controls.numberPlate.setValue(null);
          } else {
            const selectedPlateEntry = this.controlPlateDropdownValues.find(plate => plate.id === controlPlateId);
            if (selectedPlateEntry) {
              vehicleFormGroup.controls.type.setValidators([]);
              vehicleFormGroup.controls.type.setValue(selectedPlateEntry.plate.type);
              vehicleFormGroup.controls.countryCode.setValidators([]);
              vehicleFormGroup.controls.countryCode.setValue(selectedPlateEntry.plate.countryCode);
              vehicleFormGroup.controls.numberPlate.setValidators([]);
              vehicleFormGroup.controls.numberPlate.setValue(selectedPlateEntry.plate.numberPlate);
            }
          }
          vehicleFormGroup.controls.type.updateValueAndValidity();
          vehicleFormGroup.controls.countryCode.updateValueAndValidity();
          vehicleFormGroup.controls.numberPlate.updateValueAndValidity();
        })
      );
    }

    if(!this.isAutomaticParking) {
      subscriptions.add(
        this.translate.onLangChange.subscribe(() => {
          if (this.controlPlateDropdownValues.find(v => v.id === -1)) {
            this.controlPlateDropdownValues = this.controlPlateDropdownValues.map(v => {
              if (v.id === -1) {
                return {name: this.translate.instant('i18n.domain.parking.addVehicle') + '...', id: -1, plate: null};
              } else {
                return v;
              }
            });
          } else {
            this.controlPlateDropdownValues = [...this.controlPlateDropdownValues, {
              name: this.translate.instant('i18n.domain.parking.addVehicle') + '...',
              id: -1,
              plate: null
            }];
          }
        })
      );
    }
  }

  public handleParkingProcessInfo(user: UserInfoDTO, parkingProcessInfo: ParkingProcessInfoDTO, parkingForm: AutomaticParkingScheduleEditFormGroup | FormGroup<ParkingFormType>) {
    if (!parkingProcessInfo?.activeCheckinInfo) {
      // TODO: call reset
      return;
    }
    this.user = user;
    const activeCheckinInfo = parkingProcessInfo.activeCheckinInfo;
    this.createClientDropdownValues();
    parkingForm.controls.clientId.setValue(activeCheckinInfo.clientId);

    this.zoneDropdownValues = [{name: this.getZoneNameArray(activeCheckinInfo.zone), id: activeCheckinInfo.zoneId}];
    parkingForm.controls.zoneId.setValue(activeCheckinInfo.zoneId);

    this.subscriptionsDropdownValues = [{
      name: activeCheckinInfo.subscriptionAssignment?.subscription.name ? this.getSubscriptionName(activeCheckinInfo.subscriptionAssignment?.subscription.name, activeCheckinInfo.subscriptionAssignment?.subscription.company?.name) : this.translate.instant('i18n.domain.parking.no-subscription-assigned'),
      id: activeCheckinInfo.subscriptionAssignment?.id ?? -1,
      assignment: null
    }];
    parkingForm.controls.subscriptionAssignmentId.setValue(activeCheckinInfo.subscriptionAssignment?.id ?? -1);

    this.controlPlateDropdownValues = [{name: activeCheckinInfo.controlPlateNumber, id: 1, plate: null}];
    parkingForm.controls.controlPlateId.setValue(1);

    parkingForm.controls.parkingSpaceNumber.setValue(activeCheckinInfo.parkingSpaceNumber);
  }

  public initDropDownValuesForValidForm(user: UserInfoDTO, parkingForm: AutomaticParkingScheduleEditFormGroup): Observable<ParkingProcessZoneInfoDTO[]> {
    this.hasFoundZones = true;
    this.hasFoundSubscriptions = true;
    this.hasFoundControlPlates = true;
    this.user = user;
    this.createClientDropdownValues();
    if (parkingForm.controls.clientId.value) {
      this.spinnerService.show();
      return this.parkingProcessInfoService.getAutomaticParkingScheduleZoneInfos(parkingForm.controls.clientId.value).pipe(tap({
        next: parkingProcessZoneInfo => {
          this.spinnerService.reset();
          this.parkingProcessZoneInfoDTO = parkingProcessZoneInfo;
            this.createZoneDropdownValues(parkingProcessZoneInfo);
            if (this.zoneDropdownValues?.length === 1) {
              parkingForm.controls.zoneId.disable();
            } else {
              parkingForm.controls.zoneId.enable();
            }
            this.createSubscriptionDropdownValues(parkingForm.controls.zoneId.value);
            if (this.subscriptionsDropdownValues?.length === 1) {
              parkingForm.controls.subscriptionAssignmentId.disable();
            } else {
              parkingForm.controls.subscriptionAssignmentId.enable();
            }
            this.handleVehicleFields(user, parkingForm.controls.subscriptionAssignmentId.value, parkingForm.controls.zoneId.value);
            if (this.controlPlateDropdownValues?.length === 1) {
              parkingForm.controls.controlPlateId.disable();
            } else {
              parkingForm.controls.controlPlateId.enable();
            }
          },
        error: error => {this.spinnerService.reset();}}));
    }
    return of(null);
  }

  public createParkingProcessDropdownFieldsValues(user: UserInfoDTO): void {
    this.hasFoundZones = true;
    this.hasFoundSubscriptions = true;
    this.hasFoundControlPlates = true;
    this.user = user;
    this.createClientDropdownValues();
  }

  public handleVehicleFields(user: UserInfoDTO, subscriptionId: number, zoneId: number): void {
    const assignment = this.subscriptionsDropdownValues?.find(s => s.id === subscriptionId)?.assignment;
    const zoneInfo = this.parkingProcessZoneInfoDTO?.find(z => z.id === zoneId);
    if (!assignment || !zoneInfo) {
      this.controlPlateDropdownValues = [];
      return;
    }
    // TODO: switch if statement for V1.1 to support when foreign parkers are allowed
    // if (this.isAutomaticParking || !zoneInfo.foreignParkersAllowed) {
    if (true) {
      const linkedParkerTags = user.linkedParkers
        .find(parker => parker.parkerId === assignment.parkerId)?.tags;
      this.controlPlateDropdownValues = linkedParkerTags.map(tag => ({
        name: tag.controlPlateNumber?.toUpperCase(),
        id: tag.id,
        plate: {
          countryCode: tag.countryCode,
          type: tag.controlPlateType,
          numberPlate: tag.controlPlateNumber
        }
      }));
    } else {
      this.controlPlateDropdownValues = user.controlPlates.map(plate => ({
        name: plate.controlPlateNumber?.toUpperCase(),
        id: plate.id,
        plate: {
          countryCode: plate.countryCode,
          type: plate.controlPlateType,
          numberPlate: plate.controlPlateNumber
        }
      }));

      this.controlPlateDropdownValues = [...this.controlPlateDropdownValues, {
        name: this.translate.instant('i18n.domain.parking.addVehicle') + '...',
        id: -1,
        plate: null
      }];
    }
  }

  public resetZone(form: AutomaticParkingScheduleEditFormGroup | FormGroup<ParkingFormType>): void {
    form.controls.zoneId.setValue(null);
    this.resetSubscription(form);
  }

  public resetSubscription(form: AutomaticParkingScheduleEditFormGroup | FormGroup<ParkingFormType>): void {
    form.controls.subscriptionAssignmentId.setValue(null);
    if ('weekdays' in form) {
      this.resetWeekdays(form as AutomaticParkingScheduleEditFormGroup);
    }
  }

  public resetWeekdays(form: AutomaticParkingScheduleEditFormGroup): void {
    form.controls.weekdays.controls.allowedMonday.setValue(false);
    form.controls.weekdays.controls.allowedTuesday.setValue(false);
    form.controls.weekdays.controls.allowedWednesday.setValue(false);
    form.controls.weekdays.controls.allowedThursday.setValue(false);
    form.controls.weekdays.controls.allowedFriday.setValue(false);
    form.controls.weekdays.controls.allowedSaturday.setValue(false);
    form.controls.weekdays.controls.allowedSunday.setValue(false);
    this.resetTimeDuration(form);
  }

  public resetTimeDuration(form: AutomaticParkingScheduleEditFormGroup): void {
    form.controls.startTime.setValue(null);
    form.controls.duration.setValue(null);
  }

  private getZoneNameArray(zone: ParkingProcessZoneInfoDTO | ParkingProcessLimitedZoneWithParentInfoDTO, names: string[] = []): string[] {
    names.push(zone.name);
    return zone.parent ? this.getZoneNameArray(zone.parent, names) : names.reverse();
  }

  private createSubscriptionDropdownValues(zoneId: number): void {
    if (!zoneId || !this.parkingProcessZoneInfoDTO) {
      this.subscriptionsDropdownValues = [];
      return;
    }
    const zone = this.parkingProcessZoneInfoDTO.find(z => z.id === zoneId);
    let subscriptionAssignments = [];
    if (zone && zone.subscriptionAssignments) {
      subscriptionAssignments = zone.subscriptionAssignments;
    }

    this.parkingSpaceNumberDefinition = zone?.parkingSpaceNumberDefinition;

    // TODO: commented out code in sort was for AVP-501 -> deactivated again since there were massive differences with mobile app => needs clarification
    this.subscriptionsDropdownValues = subscriptionAssignments.map(s => ({name: this.getSubscriptionName(s.subscription.name, s.subscription.company?.name), id: s.id, assignment: s}))
      .sort((o1, o2) => /*o1.assignment.isFavorite != o2.assignment.isFavorite ? o2.assignment.isFavorite - o1.assignment.isFavorite : */o1.name.toLowerCase().localeCompare(o2.name.toLowerCase()));
  }

  private createZoneDropdownValues(parkingProcessZoneInfoDTO: ParkingProcessZoneInfoDTO[]): void {
    this.zoneDropdownValues = parkingProcessZoneInfoDTO.map(zone => ({
      name: this.getZoneNameArray(zone),
      id: zone.id,
    }))
      .sort((o1, o2) => o1.name.join().toLowerCase().localeCompare(o2.name.join().toLowerCase()));
  }

  private createClientDropdownValues(): void {
    this.clientDropdownValues = this.user.linkedParkers
      .filter(parker => parker.isClientManaged && parker.client?.isActive && (!this.isAutomaticParking || parker.client?.automaticParkingSchedulesEnabled))
      .map(lp => lp.client)
      .map(client => ({name: client.name, id: client.id}))
      .filter((value, index, self) => index === self.findIndex(val => val.id === value.id));
  }

  private getSubscriptionName(subscriptionName: string, companyName: string): string {
    var name = subscriptionName;
    if (companyName?.length > 0) {
      name += ' (' + companyName + ')';
    }
    return name;
  }
}
